// Minimal client-side campaign attribution. Captured once per session on first
// landing and reused for any lead submitted later in that session.
export type Attribution = {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  referrer?: string | null;
  landing_page?: string | null;
};

const KEY = "cc_attribution";

function clip(v: string | null, max: number): string | null {
  if (!v) return null;
  const t = v.trim();
  return t ? t.slice(0, max) : null;
}

/** Call once on mount of a page that can lead to a conversion. */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl: Attribution = {
      utm_source: clip(params.get("utm_source"), 120),
      utm_medium: clip(params.get("utm_medium"), 120),
      utm_campaign: clip(params.get("utm_campaign"), 160),
      utm_content: clip(params.get("utm_content"), 160),
    };
    const hasUtm = Object.values(fromUrl).some(Boolean);
    const existing = window.sessionStorage.getItem(KEY);

    // First-touch wins: don't overwrite an existing session attribution unless
    // the current URL carries fresh campaign parameters.
    if (existing && !hasUtm) return;

    const record: Attribution = {
      ...fromUrl,
      referrer: clip(document.referrer || null, 500),
      landing_page: clip(window.location.href.split("#")[0] ?? null, 500),
    };
    window.sessionStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    // sessionStorage unavailable (private mode) — attribution is best-effort.
  }
}

export function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attribution) : {};
  } catch {
    return {};
  }
}
